import { yupResolver } from "@hookform/resolvers/yup";
import { ImageUploadWithPreview, InputSelect, ParentTaskSelect, SelectMulti, TextArea, TextAreaCus, TextField } from "components";
import { useApp, useMember, usePermission, useTask, useUser } from "hooks";
import { SaveAll } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { defaultTaskValues, taskCreateSchema, ROUTES, priorityStatusOptions, repareStorageURL, formatBytes, taskTypeOptions } from "utils";
import Zoom from 'react-medium-image-zoom';

interface ITaskForm {
    onClose?: () => void;
    onConfirm?: () => void;
    item?: any;
}

export default function TaskForm({ onConfirm }: ITaskForm) {
    const router = useNavigate();
    const { onUpdateParams, taskDetail } = useApp();
    const { defaultProject } = useUser();

    const { onSearch } = useTask();

    const { register, control, handleSubmit, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(taskCreateSchema),
        defaultValues: taskDetail ? {
            ...taskDetail,
            tags: taskDetail?.tags.map(el => ({ value: el.id, label: el.name })),
        } : defaultTaskValues
    });

    const { createTask, updateTask, loadDataDepend } = useTask();

    // const { fetchDataSearch } = useMember();
    // const { data: dataMembers } = fetchDataSearch();

    const [optionsMember, setOptionsMember] = useState([])

    const [optionTags, setOptionTags] = useState([]);
    const [optionSprint, setOptionSprint] = useState([]);
    const [valTagsSelected, setValTagsSelected] = useState([]);
    const [valParentsSelected, setValParentsSelected] = useState(null);

    useEffect(() => {
        if (!defaultProject?.id) return;
        (async () => {
            const { sprints, tags } = await loadDataDepend(defaultProject?.id);
            if (sprints?.data?.length) {
                setOptionSprint([{ value: null, name: "Chọn phiên bản" }, ...sprints.data].map(el => ({ value: el.id, name: el.name })))
            }
            if (tags?.data?.length) {
                setOptionTags(tags.data.map(el => ({ value: el.id, label: el.name })))
            }
        })();
        if (defaultProject?.members) {
            setOptionsMember([{ value: null, name: "Chọn người dùng" }, ...defaultProject?.members].map(el => ({ value: el.id, name: el.name })))
        }
    }, [defaultProject?.id]);

    useEffect(() => {
        if (!taskDetail?.id) return;
        setValue('files[]', null);
        setValue('assignee', null);
    }, [taskDetail]);



    const onRedirect = () => {
        router(ROUTES.PRIVATE_ROOT.TASKS);
        onConfirm?.()
    }

    const onSubmit = (data) => {
        data = { ...data, project_id: defaultProject?.id }
        if (taskDetail) {
            updateTask.mutate({ ...data, parents_id: valParentsSelected, tags: valTagsSelected.map(e => e.value ? +e.value : null) }, {
                onSuccess: (res) => {
                    if (res.statusCode === 200) {
                        onUpdateParams({ taskDetail: res.data });
                        onRedirect();
                    }
                },
            })
            return;
        }
        createTask.mutate({ ...data, parents_id: valParentsSelected, tags: valTagsSelected.map(e => e.value ? +e.value : null) }, {
            onSuccess: (res) => {
                onRedirect();
            },
        })
    }

    const loadOptions = async (input: string) => {
        if (!input) return [];
        try {
            const { data } = await onSearch(defaultProject?.id, { q: input });
            return [{ id: null, title: 'Chọn nhiệm vụ liên quan...' }, ...data].map((task: any) => ({
                value: task.id,
                label: task.id ? `#${task.id} - ${task.title}` : `${task.title}`
            }));
        } catch (err) {
            return [];
        }
    };

    return (
        <div className="mx-auto">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="bg-white rounded-lg shadow-sm p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                        <div className="grid grid-cols-1">
                            <div>
                                <label className="block font-medium mb-1">Tiêu đề:</label>
                                <TextField
                                    type="text"
                                    placeholder="Nhập tiêu đề"
                                    {...register('title')}
                                />
                                {errors.title && <p className="text-red-500">{`${errors.title.message}`}</p>}
                            </div>
                        </div>
                        <div>
                            <label className="block font-medium mb-1">Mô tả:</label>
                            <Controller
                                control={control}
                                name="description"
                                render={({ field }) => (
                                    <TextArea
                                        {...field}
                                        placeholder="Nhập mô tả ngắn..."
                                        {...register("description")}
                                        rows={3}
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <label className="block font-medium mb-1">Nội dung:</label>
                            <Controller
                                control={control}
                                name="content"
                                render={({ field }) => (
                                    <TextAreaCus
                                        {...field}
                                        className="h-300"
                                        style={{ height: 500, paddingBottom: 50 }}
                                        placeholder="Nhập nội dung..."
                                    />

                                )}
                            />
                        </div>

                        <div className="grid grid-cols-5 gap-3">
                            <div className="">
                                <label className="block font-medium mb-1">Ngày bắt đầu:</label>
                                <TextField type="date" {...register("start_date")} />
                                {errors.start_date && <p className="text-red-500">{`${errors.start_date.message}`}</p>}
                            </div>
                            <div className="">
                                <label className="block font-medium mb-1">Ngày đến hạn:</label>
                                <TextField type="date" {...register("due_date")} />
                                {errors.due_date && <p className="text-red-500">{`${errors.due_date.message}`}</p>}
                            </div>
                            <div>
                                <label className="block font-medium mb-1">Mức độ:</label>
                                <Controller
                                    control={control}
                                    name="priority"
                                    render={({ field }) => (
                                        <InputSelect options={priorityStatusOptions} {...field} />
                                    )}
                                />

                            </div>
                            <div>
                                <label className="block font-medium mb-1">Thời gian tạm tính (h):</label>
                                <TextField
                                    type="number"
                                    className="w-[150px]"
                                    min={0}
                                    placeholder="Nhập thời gian tạm tính"
                                    {...register('est_time')}
                                />
                                {errors.est_time && <p className="text-red-500">{`${errors.est_time.message}`}</p>}
                            </div>
                            <div>
                                <label className="block font-medium mb-1">Thời gian đã làm (h):</label>
                                <TextField
                                    type="number"
                                    className="w-[150px]"
                                    min={0}
                                    placeholder="Nhập thời gian đã làm"
                                    {...register('spent_time')}
                                />
                                {errors.spent_time && <p className="text-red-500">{`${errors.spent_time.message}`}</p>}
                            </div>

                        </div>
                        <div className="grid grid-cols-5 gap-3">
                            <div>
                                <label className="block font-medium mb-1">Giao cho:</label>
                                <Controller
                                    control={control}
                                    name="assignee_id"
                                    render={({ field }) => (
                                        <InputSelect options={optionsMember} {...field} />
                                    )}
                                />
                                {errors.assignee_id && <p className="text-red-500">{`${errors.assignee_id.message}`}</p>}
                            </div>
                            <div>
                                <label className="block font-medium mb-1">Tiến độ:</label>
                                <Controller
                                    control={control}
                                    name="progress"
                                    render={({ field }) => (
                                        <InputSelect options={[...Array(11)].map((_, i) => ({ value: i * 10, name: `${i * 10}%` }))} {...field} />
                                    )}
                                />
                            </div>
                            <div>
                                <label className="block font-medium mb-1">Phiên bản:</label>
                                <Controller
                                    control={control}
                                    name="sprint_id"
                                    render={({ field }) => (
                                        <InputSelect options={optionSprint} {...field} />
                                    )}
                                />
                                {errors.sprint_id && <p className="text-red-500">{`${errors.sprint_id.message}`}</p>}
                            </div>
                            <div>
                                <label className="block font-medium mb-1">Nhiệm vụ liên quan:</label>
                                <Controller
                                    control={control}
                                    name="parents_id"
                                    render={({ field }) => (
                                        <ParentTaskSelect
                                            loadOptions={loadOptions}
                                            placeholder="Chọn nhiệm vụ liên quan"
                                            {...field}
                                            onChange={v => {
                                                setValParentsSelected(v.value)
                                                field.onChange(v)
                                            }} />
                                    )}
                                />
                            </div>
                            <div>
                                <label className="block font-medium mb-1">Loại:</label>
                                <Controller
                                    control={control}
                                    name="type"
                                    render={({ field }) => (
                                        <InputSelect options={taskTypeOptions} {...field} />
                                    )}
                                />
                            </div>
                        </div>


                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                        {!!taskDetail?.files?.length && <div>
                            <label className="block font-medium mb-1">Hình ảnh:</label>
                            <div className="space-y-2">
                                {taskDetail?.files.map(file => <div key={file.id} className="flex gap-2 items-center">
                                    <Zoom>
                                        <img className="max-w-10 p-1 rounded-md border border-gray-300" src={repareStorageURL(`${file?.path}`)} alt={file.name} />
                                    </Zoom>
                                    <div className="flex flex-col justify-center">
                                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                        <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
                                    </div>
                                </div>)}
                            </div>
                        </div>}
                        <div>
                            <label className="block font-medium mb-1">Files</label>
                            <Controller
                                control={control}
                                name="files"
                                render={({ field }) => (
                                    <ImageUploadWithPreview multiple={true} {...field} onPick={f => field.onChange(f)} />
                                )}
                            />
                        </div>
                        <div className="mt-6">
                            <div className="font-medium mb-2">Thẻ</div>
                            <Controller
                                control={control}
                                name="tags"
                                render={({ field }) => (
                                    <SelectMulti
                                        options={optionTags}
                                        {...field}
                                        onChange={v => {
                                            setValTagsSelected(v)
                                            field.onChange(v)
                                        }}
                                    />
                                )}
                            />
                        </div>
                        <button
                            type="submit"
                            className={`px-4 py-4 w-full rounded-lg text-sm font-medium transition-colors bg-green-100 text-green-700 hover:bg-green-600 hover:text-white`}
                        >
                            <div className="flex justify-center"> <SaveAll className="mr-2" /> Lưu</div>
                        </button>
                    </div>
                </div>
            </form>

        </div>
    );
}
